import { SupervisorAccount } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useState } from 'react';

import { loadDocument } from '~/auth/utils';
import { liftAiAuth } from '~/utils/api';

/**
 * Persistent warning bar shown while a super-admin is signed in as another
 * user via the impersonation tool. Pinned to the bottom of the viewport so it
 * does not collide with the fixed AppBar/sidebar, and visible on every page.
 *
 * "Exit impersonation" ends the impersonation session and signs the admin back
 * in as themselves, or sends them to sign in when their own session could not
 * be resumed.
 */
export function ImpersonationBanner() {
  const impersonation = liftAiAuth().getImpersonation();
  const [isExiting, setIsExiting] = useState(false);

  if (!impersonation) {
    return null;
  }

  const exit = async () => {
    setIsExiting(true);
    try {
      const user = await liftAiAuth().exitImpersonation();
      loadDocument(user ? '/' : '/login');
    } catch {
      // Signing out clears whatever the failed exit left behind.
      loadDocument('/logout');
    }
  };

  return (
    <Box
      role="status"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.drawer + 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: 2,
        px: 2,
        py: 1,
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        boxShadow: 6,
      }}
    >
      <SupervisorAccount fontSize="small" />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        You are impersonating{' '}
        {impersonation.impersonatedEmail ? (
          <strong>{impersonation.impersonatedEmail}</strong>
        ) : (
          'another user'
        )}
        . Anything you do is performed as this user.
      </Typography>
      <Button
        onClick={() => void exit()}
        loading={isExiting}
        size="small"
        variant="outlined"
        color="inherit"
        startIcon={<SupervisorAccount />}
      >
        Exit impersonation
      </Button>
    </Box>
  );
}
